import { inject, Injectable } from "@angular/core";
import {
  ApiCursusPayload,
  ApiExamenonderdeelPayload,
  ApiMinorExamenonderdeelPayload,
  ApiPeriodePayload,
  ApiPlanAndPlanLaterPayload,
  ApiPlanLaterLosseCursussenPayload,
  ApiPlanLaterMinorsPayload,
  ApiPlanLaterProfileringPayload,
  ApiSpecialisatiePayload,
  ApiSpecialisatieruimtePayload,
  ApiStudiejaarPayload,
} from "@app/core/api/model/api-plan-payload";
import { Period, PlanDetails, StudyYear } from "@app/core/domain/plan-details";
import { Course } from "@core/domain/course";
import { ExamComponent, Minor, MinorExamComponent } from "@core/domain/exam-component";
import { ApiHelper } from "@core/domain/helpers/api-helper";
import { PlanLater, PlanLaterElectives, Specialisation, Specialisations } from "@core/domain/plan-later";
import { ApiToetsIdentificatie } from "../api/model/api-toets";
import { Constants } from "../constants";
import { CourseHelper } from "../domain/helpers/course-helper";

@Injectable({
  providedIn: "root",
})
export class PlanPayloadMappingService {
  private readonly courseHelper = inject(CourseHelper);

  public buildApiPlanAndPlanLaterPayload(plan: PlanDetails, planLater: PlanLater): ApiPlanAndPlanLaterPayload {
    const includeExamComponents = plan.allowsChangeExamComponent;
    const includeTests = plan.allowsTestPlanning;
    return {
      spla_id: plan.id,
      examenprogramma: plan.studyProgram,
      tweede_examenprogramma: plan.secondStudyProgram,
      studiejaren: plan.studyYears.map((year) => this.mapToApiStudiejaar(year, includeExamComponents, includeTests)),
      losse_cursussen: this.mapToApiPlanLaterLosseCursussen(planLater.otherCourses),
      profilering: this.mapToApiPlanLaterProfilering(planLater.electives),
      specialisatieruimte: this.mapToApiSpecialisatieRuimte(planLater.specialisation),
    };
  }

  private mapToApiStudiejaar(
    year: StudyYear,
    includeExamComponents: boolean,
    includeTests: boolean,
  ): ApiStudiejaarPayload {
    return {
      studiejaar: year.yearNr,
      periodes: year.periods.map((period) => this.mapToApiPeriode(period, includeExamComponents, includeTests)),
    };
  }

  private mapToApiPeriode(period: Period, includeExamComponents: boolean, includeTests: boolean): ApiPeriodePayload {
    const apiPeriode: ApiPeriodePayload = {
      periode_nr: period.periodNr,
      periode_oms: period.periodDescription,
      cursussen:
        period.courses
          ?.filter((course) => course.ribbonNr <= 1)
          .map((course) => this.mapToApiCursus(course, includeExamComponents)) ?? [],
    };

    if (includeTests) {
      apiPeriode.toetsen = this.mapToApiToetsen(period);
    }

    return apiPeriode;
  }

  private mapToApiToetsen(period: Period): ApiToetsIdentificatie[] {
    const testsFromCourses = period.courses?.flatMap((course) => course.courseTests ?? []) ?? [];
    const testsFromBlocks =
      period.courseTestBlocks?.flatMap((courseTestBlock) => courseTestBlock.courseTests ?? []) ?? [];

    return [...testsFromCourses, ...testsFromBlocks].map((courseTest) => ({
      cursus: courseTest.courseCode,
      toets: courseTest.testCode,
    }));
  }

  private filterPlanLaterCourses(courses: Course[]): Course[] {
    if (!courses) {
      return [];
    }
    return courses.filter(
      (course) =>
        // Custom courses and courses at another institution cannot actively be moved to plan later.
        // If the plan allows changing exam components, they might show in Plan Later,
        // but we don't want to send them as part of the Plan Later when saving the plan,
        // because they should only appear in Plan Later based on their presence in the plan grid.
        // For example, when a custom course is deleted from the plan grid, we don't want to keep it in Plan Later.
        course.ribbonNr <= 1 && !this.courseHelper.isCustomCourse(course) && !course.isCourseAtAnotherInstitution,
    );
  }

  private mapToApiPlanLaterLosseCursussen(otherCoursesPlanLater: Course[]): ApiPlanLaterLosseCursussenPayload {
    return {
      cursussen: this.filterPlanLaterCourses(otherCoursesPlanLater).map((course) => this.mapToApiCursus(course)) ?? [],
    };
  }

  private mapToApiCursus(course: Course, includeExamComponents = false): ApiCursusPayload {
    const apiCursusPayload: ApiCursusPayload = {
      cursus: course.code,
      korte_naam: course.name,
      eigen_cursus: course.customCourseName,
      eigen_cursus_punten: course.customCoursePoints ?? null,
      lint_periodes: ApiHelper.numberArrayToString(course.ribbonPeriods),
      plan_later: ApiHelper.booleanToCaciBoolean(course.isInPlanLater),
      spcu_id: course.id,
    };
    if (includeExamComponents) {
      if (!course.examComponentCode || course.examComponentCode === "") {
        // if we don't have a valid exam component code, place it in the 'other' component
        apiCursusPayload.examenonderdeel = Constants.EXAM_COMPONENT_OTHER;
      } else {
        apiCursusPayload.examenonderdeel = course.examComponentCode;
      }
      if (apiCursusPayload.examenonderdeel !== Constants.EXAM_COMPONENT_OTHER) {
        // for actual exam components we also want to pass the study program to which it belongs,
        // because the plan may contain two study programs
        apiCursusPayload.examenprogramma = course.studyProgram;
        // also pass the specialisation or minor study program if available,
        // because the same exam component code could exist in both the main study program
        // and the specialisation study program or (another) minor study program
        apiCursusPayload.examenprogramma_specialisatie = course.specialisationProgram;
        apiCursusPayload.examenprogramma_minor = course.minorProgram;
      }
    }
    return apiCursusPayload;
  }

  private mapToApiPlanLaterProfilering(planLaterElectives: PlanLaterElectives): ApiPlanLaterProfileringPayload {
    return {
      minors: planLaterElectives.minors.map((minor) => this.mapToApiPlanLaterMinors(minor)),
      cursussen:
        this.filterPlanLaterCourses(planLaterElectives.courses).map((course) => this.mapToApiCursus(course)) ?? [],
    };
  }

  private mapToApiPlanLaterMinors(minor: Minor): ApiPlanLaterMinorsPayload {
    return {
      examenonderdelen: minor.examComponents.map((examComponent) => this.mapToApiMinorExamenonderdeel(examComponent)),
      minor: minor.minor,
      minor_examenprogramma: minor.studyProgram,
    };
  }

  private mapToApiMinorExamenonderdeel(examComponent: MinorExamComponent): ApiMinorExamenonderdeelPayload {
    return {
      sple_id: examComponent.id,
      examenonderdeel: examComponent.examComponentCode,
      minor_examenonderdeel: examComponent.minorExamComponentCode,
    };
  }

  private mapToApiSpecialisatieRuimte(specialisations: Specialisations | undefined): ApiSpecialisatieruimtePayload {
    return {
      specialisaties:
        specialisations?.specialisations.map((specialisation) => this.mapToApiSpecialisatie(specialisation)) ?? [],
    };
  }

  private mapToApiSpecialisatie(specialisation: Specialisation): ApiSpecialisatiePayload {
    return {
      examenonderdelen: specialisation.examComponents.map((examComponent) =>
        this.mapToApiExamenonderdeel(examComponent),
      ),
      specialisatie: specialisation.code,
      spec_examenprogramma: specialisation.studyProgramCode,
    };
  }

  private mapToApiExamenonderdeel(examComponent: ExamComponent): ApiExamenonderdeelPayload {
    return {
      sple_id: examComponent.id,
      examenonderdeel: examComponent.examComponentCode,
      spec_examenonderdeel: examComponent.examComponentCode,
    };
  }
}
